import { useState, useEffect } from 'react';
import { useTerminal } from '@/layouts/LocalStoreLayout';
import { localPosAPI } from '@/services/localApi';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Search, Receipt, Eye, Printer, Calendar,
  Banknote, CreditCard, Smartphone, Cloud, CloudOff
} from 'lucide-react';

const LocalSalesHistory = () => {
  const { terminalId, storeInfo } = useTerminal();

  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSale, setSelectedSale] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    loadSales();
  }, [terminalId]);

  const loadSales = async () => {
    try {
      setLoading(true);
      const res = await localPosAPI.listSales({
        terminal_id: terminalId,
        limit: 100
      });
      setSales(res.data.sales || []);
    } catch (error) {
      console.error('Failed to load sales:', error);
      toast.error('Failed to load sales history');
    } finally {
      setLoading(false);
    }
  };

  const handleViewSale = async (billNumber) => {
    try {
      const res = await localPosAPI.getSale(billNumber);
      setSelectedSale(res.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to load sale details');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPaymentIcon = (mode) => {
    switch (mode) {
      case 'cash':
        return <Banknote className="w-4 h-4 text-green-600" />;
      case 'card':
        return <CreditCard className="w-4 h-4 text-blue-600" />;
      case 'upi':
        return <Smartphone className="w-4 h-4 text-purple-600" />;
      default:
        return <Banknote className="w-4 h-4" />;
    }
  };

  const filteredSales = sales.filter(sale =>
    sale.bill_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (sale.customer_name && sale.customer_name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="p-6 space-y-6" data-testid="local-sales-history-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales History</h1>
          <p className="text-gray-500">Terminal: {terminalId}</p>
        </div>
        <Button variant="outline" onClick={loadSales}>
          Refresh
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by bill number or customer..."
          className="pl-10"
        />
      </div>

      {/* Sales Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            Recent Sales ({filteredSales.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : filteredSales.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No sales found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bill No.</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Sync</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSales.map((sale) => {
                  const items = sale.items_json ? JSON.parse(sale.items_json) : sale.items || [];
                  return (
                    <TableRow key={sale.id || sale.bill_number}>
                      <TableCell className="font-mono font-medium">
                        {sale.bill_number}
                      </TableCell>
                      <TableCell className="text-gray-500">
                        {formatDateTime(sale.bill_datetime)}
                      </TableCell>
                      <TableCell>
                        {sale.customer_name || <span className="text-gray-400">Guest</span>}
                      </TableCell>
                      <TableCell>
                        {items.length} item{items.length !== 1 ? 's' : ''}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {getPaymentIcon(sale.payment_mode)}
                          <span className="capitalize">{sale.payment_mode}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        {formatCurrency(sale.total_amount)}
                      </TableCell>
                      <TableCell>
                        {sale.is_synced ? (
                          <div className="flex items-center gap-1 text-green-600">
                            <Cloud className="w-4 h-4" />
                            <span className="text-xs">Synced</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-yellow-600">
                            <CloudOff className="w-4 h-4" />
                            <span className="text-xs">Pending</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewSale(sale.bill_number)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Sale Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bill Details</DialogTitle>
          </DialogHeader>
          {selectedSale && (
            <div className="space-y-4">
              {/* Bill Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Bill Number</p>
                    <p className="font-mono font-bold text-lg">{selectedSale.bill_number}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Date & Time</p>
                    <p className="font-medium">{formatDateTime(selectedSale.bill_datetime)}</p>
                  </div>
                </div>
                {selectedSale.customer_name && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-sm text-gray-500">Customer</p>
                    <p className="font-medium">{selectedSale.customer_name}</p>
                    {selectedSale.customer_mobile && (
                      <p className="text-sm text-gray-500">{selectedSale.customer_mobile}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Items */}
              <div>
                <p className="text-sm font-semibold text-gray-500 mb-2">ITEMS</p>
                <div className="space-y-2">
                  {selectedSale.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center bg-gray-50 rounded p-2">
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-sm text-gray-500">
                          ₹{item.selling_price} × {item.quantity}
                        </p>
                      </div>
                      <p className="font-bold">₹{item.total?.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>{formatCurrency(selectedSale.subtotal)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Tax</span>
                  <span>{formatCurrency(selectedSale.tax_amount)}</span>
                </div>
                <div className="flex justify-between text-xl font-bold">
                  <span>Total</span>
                  <span>{formatCurrency(selectedSale.total_amount)}</span>
                </div>
              </div>

              {/* Payment */}
              <div className="flex items-center justify-between bg-green-50 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  {getPaymentIcon(selectedSale.payment_mode)}
                  <span className="capitalize font-medium">{selectedSale.payment_mode}</span>
                </div>
                <Badge variant="secondary" className="bg-green-200 text-green-800">
                  PAID
                </Badge>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocalSalesHistory;
